// produits/[id].tsx
//
// ÉCRAN PUBLIC — reconstruit à partir de zéro pour fonctionner SANS
// session active (visiteurs non connectés inclus). Contrairement à
// l'ancienne version, ce fichier ne délègue plus à un composant séparé
// (DetailProduit.tsx) : tout le fetch ET tout le rendu vivent ici, dans
// ce seul fichier, par choix explicite.
//
// SOURCE DE DONNÉES : POST vers /url-public-market (backend, voir
// handleUrlPublicMarket.ts), action 'get-product-public', avec
// { product_id: id }. AUCUN access_token envoyé — cette route ne
// vérifie aucune session (voir routes.ts : /url-public-market est
// déclarée hors du middleware requireAuth). Distinct de l'ancien
// useProduct(id) qui passait par /market (protégée par requireAuth) —
// celui-ci reste utilisé ailleurs pour les écrans qui nécessitent une
// session, jamais touché ici.
//
// ACTIONS DISPONIBLES SANS SESSION :
//   - WhatsApp (wa.me/...)      → fonctionne normalement, aucune session requise
//   - Acheter (buy_url)         → idem
//   - En savoir plus (learn_more_url) → idem
//   - Appeler (tel:...)         → idem
//   - Aller en boutique         → navigue vers boutiques/[slug].tsx (public)
//
// ACTIONS RETIRÉES (nécessitaient une session) :
//   - "Contacter le vendeur" (messagerie interne, show_platform_chat_button)
//     → retiré : useContactSeller suppose un utilisateur authentifié
//     (conversation privée liée à un user_id). Si show_whatsapp_business_button
//     est aussi désactivé pour ce produit, aucun bouton de contact
//     n'apparaît ici — c'est un choix assumé plutôt que d'afficher un
//     bouton qui échouerait silencieusement pour un visiteur anonyme.
//   - Favoris (cœur sur l'image) → retiré : useFavorites suppose un
//     user_id. Pas de remplacement ici (pas de redirection demandée
//     pour cette action précise, contrairement à "s'abonner" dans
//     boutiques/[slug].tsx).
//
// AUTOSCROLL + DOUBLE TAP : même système que partout ailleurs dans
// Market (useScrollAutoHide + useTabBarVisibility) — pas de header
// interne à animer ici, seule la tab bar globale est cachée/montrée au
// scroll ou au double tap (voir DetailProduit.tsx, patch V17, pour le
// même pattern).
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation /
// t()). Ajout d'un sélecteur de langue flottant en haut à droite (même
// système que landing.tsx : i18n.changeLanguage + saveLanguage), car cet
// écran public n'a pas de navbar existante pour l'y intégrer. Icônes,
// couleurs, IDs, endpoints et structure de données restent codés en
// dur, inchangés.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  Linking,
  Image,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useTranslation } from 'react-i18next';

import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';
import { saveLanguage } from '@/services/storage';

const NATIVE = Platform.OS !== 'web';
const API_BASE_URL = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';

// ── Langues disponibles pour le sélecteur (même liste que landing.tsx) ──
const LANGUAGES = [
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' },
  { code: 'pt', flag: '🇵🇹', label: 'PT' },
];

// Forme du produit telle que renvoyée par get-product-public (voir
// handleUrlPublicMarket.ts) — aplatie (shop_name/shop_slug directement
// sur l'objet, pas de structure imbriquée market_shops).
type PublicProduct = {
  id: string;
  shop_id: string;
  shop_name: string | null;
  shop_slug: string | null;
  whatsapp_business_phone: string | null;
  category_id: string;
  name: string;
  slug: string;
  description_short: string | null;
  description: string | null;
  brand: string | null;
  condition: 'new' | 'used' | 'refurbished';
  cover_image_url: string | null;
  image_2_url: string | null;
  image_3_url: string | null;
  price: number;
  promo_price: number | null;
  currency_code: string;
  stock_quantity: number;
  is_featured: boolean;
  show_whatsapp_business_button: boolean;
  whatsapp_business_phone_override: string | null;
  show_platform_chat_button: boolean;
  show_buy_button: boolean;
  buy_url: string | null;
  show_learn_more_button: boolean;
  learn_more_url: string | null;
  show_call_button: boolean;
  call_phone: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  published_at: string | null;
};

function formatPrice(amount: number, currency: string) {
  return `${amount.toLocaleString('fr-FR')} ${currency}`;
}

// ── Sélecteur de langue flottant (même système que landing.tsx) ──
function FloatingLanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const currentLanguage = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleChangeLanguage = useCallback(async (code: string) => {
    await i18n.changeLanguage(code);
    await saveLanguage(code);
    setOpen(false);
  }, [i18n]);

  return (
    <>
      <TouchableOpacity
        onPress={() => setOpen((v) => !v)}
        style={langStyles.trigger}
        activeOpacity={0.8}
      >
        <Text style={langStyles.flag}>{currentLanguage.flag}</Text>
        <Text style={langStyles.label}>{currentLanguage.label}</Text>
        <Ionicons name="chevron-down" size={12} color="#6610B1" />
      </TouchableOpacity>

      {open && (
        <TouchableOpacity
          style={langStyles.backdrop}
          activeOpacity={1}
          onPress={() => setOpen(false)}
        >
          <View style={langStyles.menu}>
            {LANGUAGES.map((lang) => (
              <TouchableOpacity
                key={lang.code}
                onPress={() => handleChangeLanguage(lang.code)}
                style={[
                  langStyles.menuItem,
                  lang.code === currentLanguage.code && langStyles.menuItemActive,
                ]}
              >
                <Text style={langStyles.menuFlag}>{lang.flag}</Text>
                <Text style={langStyles.menuLabel}>{lang.label}</Text>
                {lang.code === currentLanguage.code && (
                  <Ionicons name="checkmark" size={16} color="#6610B1" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      )}
    </>
  );
}

export default function PublicProductDetailScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();

  // ── Fetch direct, inline, sans session ──
  const [product, setProduct] = useState<PublicProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const response = await fetch(`${API_BASE_URL}/url-public-market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-product-public', product_id: id }),
      });

      if (response.status === 404) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const json = await response.json();

      if (!response.ok || !json.success) {
        setError(json.error || t('publicProductScreen.errors.cannotLoadProduct'));
        setLoading(false);
        return;
      }

      setProduct(json.product);
    } catch (err) {
      setError(t('publicProductScreen.errors.cannotReachServer'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // ── AutoScroll + double tap (tab bar uniquement, pas de header interne) ──
  const { setTabBarHidden } = useTabBarVisibility();
  const tabBarHiddenRef = useRef(false);
  const [lastTap, setLastTap] = useState<number | null>(null);

  const showTabBar = useCallback(() => {
    if (tabBarHiddenRef.current) {
      tabBarHiddenRef.current = false;
      setTabBarHidden(false);
    }
  }, [setTabBarHidden]);

  const hideTabBar = useCallback(() => {
    if (!tabBarHiddenRef.current) {
      tabBarHiddenRef.current = true;
      setTabBarHidden(true);
    }
  }, [setTabBarHidden]);

  const { handleScroll } = useScrollAutoHide({
    onShow: showTabBar,
    onHide: hideTabBar,
  });

  const toggleTabBar = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (tabBarHiddenRef.current) showTabBar();
    else hideTabBar();
  }, [showTabBar, hideTabBar]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleTabBar();
    else setLastTap(now);
  }, [lastTap, toggleTabBar]);

  // ── Images : un état d'échec par image, indépendants ──
  const [coverFailed, setCoverFailed] = useState(false);
  const [thumb2Failed, setThumb2Failed] = useState(false);
  const [thumb3Failed, setThumb3Failed] = useState(false);

  const showCover  = !!product?.cover_image_url && !coverFailed;
  const showThumb2 = !!product?.image_2_url && !thumb2Failed;
  const showThumb3 = !!product?.image_3_url && !thumb3Failed;

  // ── Actions (aucune ne nécessite de session) ──
  const handleWhatsApp = useCallback(() => {
    if (!product) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    // Priorité au numéro override du produit, sinon celui de la boutique.
    const phone = product.whatsapp_business_phone_override || product.whatsapp_business_phone;
    if (!phone) return;
    const cleanPhone = phone.replace(/[^\d]/g, '');
    const message = encodeURIComponent(t('publicProductScreen.whatsappMessage', { name: product.name }));
    Linking.openURL(`https://wa.me/${cleanPhone}?text=${message}`);
  }, [product, t]);

  const handleBuy = useCallback(() => {
    if (!product?.buy_url) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Linking.openURL(product.buy_url);
  }, [product]);

  const handleLearnMore = useCallback(() => {
    if (!product?.learn_more_url) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Linking.openURL(product.learn_more_url);
  }, [product]);

  const handleCall = useCallback(() => {
    if (!product?.call_phone) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Linking.openURL(`tel:${product.call_phone}`);
  }, [product]);

  const handleGoToShop = useCallback(() => {
    if (!product?.shop_slug) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/boutiques/${product.shop_slug}`);
  }, [router, product]);

  // WhatsApp prioritaire si un numéro exploitable existe (override ou
  // boutique) ET que le produit l'active. Pas de repli "Contacter le
  // vendeur" ici (retiré, nécessite une session — voir en-tête).
  const showWhatsApp = product?.show_whatsapp_business_button &&
    !!(product?.whatsapp_business_phone_override || product?.whatsapp_business_phone);

  // Fallback SEO — jamais de balise vide côté web.
  const metaTitle = product?.seo_title?.trim() || product?.name || t('publicProductScreen.defaultMetaTitle');
  const metaDescription = product?.seo_description?.trim() || product?.description_short?.trim() || undefined;
  const metaImage = product?.og_image_url?.trim() || product?.cover_image_url || undefined;

  // ── États de chargement / erreur ──

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <FloatingLanguagePicker />
        <ActivityIndicator size="small" color="#6610B1" />
        <Text style={styles.centerText}>{t('publicProductScreen.loadingProduct')}</Text>
      </View>
    );
  }

  if (notFound) {
    return (
      <View style={styles.centerContainer}>
        <FloatingLanguagePicker />
        <Ionicons name="alert-circle-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>{t('publicProductScreen.errors.productNotFound')}</Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => router.back()} activeOpacity={0.8}>
          <Text style={styles.backBtnText}>{t('publicProductScreen.back')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (error || !product) {
    return (
      <View style={styles.centerContainer}>
        <FloatingLanguagePicker />
        <Ionicons name="cloud-offline-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>{error || t('publicProductScreen.errors.cannotLoadProduct')}</Text>
        <TouchableOpacity style={styles.backBtn} onPress={fetchProduct} activeOpacity={0.8}>
          <Text style={styles.backBtnText}>{t('publicProductScreen.retry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {!!metaDescription && <meta name="description" content={metaDescription} />}
        <meta property="og:title" content={metaTitle} />
        {!!metaDescription && <meta property="og:description" content={metaDescription} />}
        {!!metaImage && <meta property="og:image" content={metaImage} />}
        <meta property="og:type" content="product" />
      </Head>
      <FloatingLanguagePicker />
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
      >
        {/* ── Galerie ── */}
        {showCover ? (
          <View style={styles.imageWrap}>
            <Image
              source={{ uri: product.cover_image_url! }}
              style={styles.image}
              resizeMode="cover"
              onError={() => setCoverFailed(true)}
            />
            {product.is_featured && (
              <View style={styles.featuredBadge}>
                <Text style={styles.featuredBadgeText}>{t('publicProductScreen.newBadge')}</Text>
              </View>
            )}
          </View>
        ) : (
          <View style={styles.imagePlaceholder}>
            <Ionicons name="image-outline" size={48} color="#C4B5FD" />
            {product.is_featured && (
              <View style={styles.featuredBadge}>
                <Text style={styles.featuredBadgeText}>{t('publicProductScreen.newBadge')}</Text>
              </View>
            )}
          </View>
        )}

        {(product.image_2_url || product.image_3_url) && (
          <View style={styles.thumbRow}>
            {product.image_2_url && (
              showThumb2 ? (
                <Image
                  source={{ uri: product.image_2_url }}
                  style={styles.thumb}
                  resizeMode="cover"
                  onError={() => setThumb2Failed(true)}
                />
              ) : (
                <View style={styles.thumbPlaceholder}>
                  <Ionicons name="image-outline" size={22} color="#C4B5FD" />
                </View>
              )
            )}
            {product.image_3_url && (
              showThumb3 ? (
                <Image
                  source={{ uri: product.image_3_url }}
                  style={styles.thumb}
                  resizeMode="cover"
                  onError={() => setThumb3Failed(true)}
                />
              ) : (
                <View style={styles.thumbPlaceholder}>
                  <Ionicons name="image-outline" size={22} color="#C4B5FD" />
                </View>
              )
            )}
          </View>
        )}

        {/* ── Nom, boutique, résumé, prix ── */}
        <Text style={styles.name}>{product.name}</Text>
        {!!product.shop_name && (
          <TouchableOpacity onPress={handleGoToShop} activeOpacity={0.7}>
            <Text style={styles.shopName}>{product.shop_name}</Text>
          </TouchableOpacity>
        )}

        {!!product.description_short && (
          <Text style={styles.descriptionShort}>{product.description_short}</Text>
        )}

        <View style={styles.priceRow}>
          {product.promo_price ? (
            <>
              <Text style={styles.promoPrice}>{formatPrice(product.promo_price, product.currency_code)}</Text>
              <Text style={styles.originalPrice}>{formatPrice(product.price, product.currency_code)}</Text>
            </>
          ) : (
            <Text style={styles.price}>{formatPrice(product.price, product.currency_code)}</Text>
          )}
        </View>

        {product.stock_quantity > 0 ? (
          <View style={styles.stockRow}>
            <Ionicons name="checkmark-circle" size={14} color="#10B981" />
            <Text style={styles.stockTextAvailable}>{t('publicProductScreen.inStock', { count: product.stock_quantity })}</Text>
          </View>
        ) : (
          <View style={styles.stockRow}>
            <Ionicons name="close-circle" size={14} color="#EF4444" />
            <Text style={styles.stockTextUnavailable}>{t('publicProductScreen.outOfStock')}</Text>
          </View>
        )}

        {/* ── Description ── */}
        {!!product.description && (
          <View style={styles.descriptionBlock}>
            <Text style={styles.descriptionTitle}>{t('publicProductScreen.descriptionTitle')}</Text>
            <Text style={styles.descriptionText}>{product.description}</Text>
          </View>
        )}

        {/* ── Infos secondaires ── */}
        {(product.brand || product.condition) && (
          <View style={styles.metaRow}>
            {!!product.brand && (
              <View style={styles.metaChip}>
                <Text style={styles.metaChipText}>{product.brand}</Text>
              </View>
            )}
            <View style={styles.metaChip}>
              <Text style={styles.metaChipText}>
                {product.condition === 'new' ? t('publicProductScreen.condition.new') : product.condition === 'used' ? t('publicProductScreen.condition.used') : t('publicProductScreen.condition.refurbished')}
              </Text>
            </View>
          </View>
        )}

        {/* ── Boutons d'action (sans session) ── */}
        <View style={styles.actionsBlock}>
          {showWhatsApp && (
            <TouchableOpacity style={styles.primaryBtn} activeOpacity={0.8} onPress={handleWhatsApp}>
              <Ionicons name="logo-whatsapp" size={18} color="#fff" />
              <Text style={styles.primaryBtnText}>{t('publicProductScreen.contactViaWhatsApp')}</Text>
            </TouchableOpacity>
          )}

          {product.show_buy_button && (
            <TouchableOpacity style={styles.secondaryBtn} activeOpacity={0.8} onPress={handleBuy}>
              <Ionicons name="cart-outline" size={18} color="#6610B1" />
              <Text style={styles.secondaryBtnText}>{t('publicProductScreen.buy')}</Text>
            </TouchableOpacity>
          )}

          {product.show_learn_more_button && (
            <TouchableOpacity style={styles.secondaryBtn} activeOpacity={0.8} onPress={handleLearnMore}>
              <Ionicons name="information-circle-outline" size={18} color="#6610B1" />
              <Text style={styles.secondaryBtnText}>{t('publicProductScreen.learnMore')}</Text>
            </TouchableOpacity>
          )}

          {product.show_call_button && (
            <TouchableOpacity style={styles.secondaryBtn} activeOpacity={0.8} onPress={handleCall}>
              <Ionicons name="call-outline" size={18} color="#6610B1" />
              <Text style={styles.secondaryBtnText}>{t('publicProductScreen.call')}</Text>
            </TouchableOpacity>
          )}

          {!!product.shop_slug && (
            <TouchableOpacity style={styles.tertiaryBtn} activeOpacity={0.8} onPress={handleGoToShop}>
              <Ionicons name="storefront-outline" size={18} color="#6610B1" />
              <Text style={styles.tertiaryBtnText}>{t('publicProductScreen.goToShop')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
    backgroundColor: '#fff',
  },
  centerText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  backBtn: {
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#F3E8FF',
    borderRadius: 10,
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#6610B1',
  },
  imageWrap: {
    width: '100%',
    height: 240,
    borderRadius: 16,
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundColor: '#F3E8FF',
  },
  imagePlaceholder: {
    width: '100%',
    height: 240,
    borderRadius: 16,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: '#6610B1',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  featuredBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#fff',
  },
  thumbRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  thumb: {
    width: 60,
    height: 60,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
  },
  thumbPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  name: {
    fontSize: 20,
    fontWeight: '800',
    color: '#222',
    marginTop: 18,
  },
  shopName: {
    fontSize: 14,
    color: '#6610B1',
    fontWeight: '600',
    marginTop: 4,
  },
  descriptionShort: {
    fontSize: 13.5,
    color: '#666',
    marginTop: 8,
    lineHeight: 19,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    marginTop: 12,
  },
  price: {
    fontSize: 22,
    fontWeight: '800',
    color: '#6610B1',
  },
  promoPrice: {
    fontSize: 22,
    fontWeight: '800',
    color: '#6610B1',
  },
  originalPrice: {
    fontSize: 15,
    color: '#999',
    textDecorationLine: 'line-through',
  },
  stockRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 8,
  },
  stockTextAvailable: {
    fontSize: 12.5,
    color: '#10B981',
    fontWeight: '600',
  },
  stockTextUnavailable: {
    fontSize: 12.5,
    color: '#EF4444',
    fontWeight: '600',
  },
  descriptionBlock: {
    marginTop: 22,
  },
  descriptionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  descriptionText: {
    fontSize: 14,
    color: '#444',
    lineHeight: 21,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 16,
  },
  metaChip: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  metaChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
  },
  actionsBlock: {
    marginTop: 26,
    gap: 10,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#25D366',
    paddingVertical: 14,
    borderRadius: 12,
  },
  primaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#fff',
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#F3E8FF',
    paddingVertical: 14,
    borderRadius: 12,
  },
  secondaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#6610B1',
  },
  tertiaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#fff',
    borderWidth: 1.5,
    borderColor: '#6610B1',
    paddingVertical: 14,
    borderRadius: 12,
  },
  tertiaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#6610B1',
  },
});

// ── Styles du sélecteur de langue flottant ──
const langStyles = StyleSheet.create({
  trigger: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 54 : 30,
    right: 16,
    zIndex: 200,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E8DEFF',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 4,
  },
  flag: { fontSize: 14 },
  label: { color: '#6610B1', fontSize: 12, fontWeight: '700' },
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 199,
  },
  menu: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 96 : 72,
    right: 16,
    backgroundColor: '#fff',
    borderRadius: 14,
    paddingVertical: 6,
    minWidth: 130,
    borderWidth: 1,
    borderColor: '#E8DEFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 10,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  menuItemActive: { backgroundColor: '#F3E8FF' },
  menuFlag: { fontSize: 15 },
  menuLabel: { flex: 1, fontSize: 13, fontWeight: '600', color: '#333' },
});
